import React, { useState, useEffect, useCallback } from 'react'
import {
  Badge, Dropdown, Button, Tag, Empty, Spin, Tooltip,
} from 'antd'
import {
  BellOutlined, CheckOutlined, CheckCircleOutlined,
} from '@ant-design/icons'
import API from 'Util/Axios'

const NIVEL_LABEL = { 1: 'Info', 2: 'Acción requerida', 3: 'Checklist' }
const NIVEL_COLOR = { 1: 'default', 2: 'orange', 3: 'green' }

export default function NotificationBell() {
  const [count,   setCount]   = useState(0)
  const [notifs,  setNotifs]  = useState([])
  const [open,    setOpen]    = useState(false)
  const [loading, setLoading] = useState(false)

  // ── Cargar solo el conteo (polling) ─────────────────────────────────────────
  const loadCount = useCallback(() => {
    API.get('notification/unread-count')
      .then(({ data }) => setCount(data.count || 0))
      .catch(() => {})
  }, [])

  // ── Cargar lista completa (al abrir el dropdown) ─────────────────────────────
  const loadNotifs = useCallback(() => {
    setLoading(true)
    API.get('notification', { params: { limite: 15 } })
      .then(({ data }) => {
        setNotifs(data.docs || [])
        setCount(data.noLeidas || 0)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  // Polling cada 30 segundos
  useEffect(() => {
    loadCount()
    const interval = setInterval(loadCount, 30000)
    return () => clearInterval(interval)
  }, [loadCount])

  // Socket — tiempo real
  useEffect(() => {
    const sock = window.socket
    if (!sock) return
    const handler = (n) => {
      setCount(c => c + 1)
      setNotifs(prev => [n, ...prev.slice(0, 14)])
    }
    sock.on('nueva_notificacion', handler)
    return () => sock.off('nueva_notificacion', handler)
  }, [])

  // ── Acciones ─────────────────────────────────────────────────────────────────
  const marcarLeida = (id) => {
    API.put(`notification/${id}/leer`).catch(() => {})
    setNotifs(prev => prev.map(n => n._id === id ? { ...n, leida: true } : n))
    setCount(c => Math.max(0, c - 1))
  }

  const marcarTodas = () => {
    API.put('notification/leer-todas').catch(() => {})
    setNotifs(prev => prev.map(n => ({ ...n, leida: true })))
    setCount(0)
  }

  const confirmar = (id, e) => {
    e.stopPropagation()
    API.put(`notification/${id}/confirmar`).catch(() => {})
    setNotifs(prev => prev.map(n =>
      n._id === id ? { ...n, confirmada: true, leida: true } : n
    ))
    setCount(c => Math.max(0, c - 1))
  }

  // ── Render del panel ─────────────────────────────────────────────────────────
  const panel = (
    <div style={{
      width: 380, maxHeight: 520, background: '#fff',
      borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
      overflow: 'hidden', display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px', borderBottom: '1px solid #F0F0F0',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>
          Notificaciones{count > 0 && <Tag color="blue" style={{ marginLeft: 8 }}>{count} nuevas</Tag>}
        </span>
        {count > 0 && (
          <Button type="link" size="small" onClick={marcarTodas}>
            Marcar todas leídas
          </Button>
        )}
      </div>

      {/* Lista */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>
        ) : notifs.length === 0 ? (
          <Empty description="Sin notificaciones" style={{ padding: 32 }} />
        ) : (
          notifs.map(n => (
            <div
              key={n._id}
              onClick={() => !n.leida && marcarLeida(n._id)}
              style={{
                padding: '12px 16px',
                background: n.leida ? '#fff' : '#F0F7FF',
                borderBottom: '1px solid #F5F5F5',
                cursor: n.leida ? 'default' : 'pointer',
                transition: 'background 0.2s',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, gap: 6 }}>
                <span style={{ fontWeight: n.leida ? 400 : 600, fontSize: 13, flex: 1 }}>
                  {n.titulo}
                </span>
                <Tag color={NIVEL_COLOR[n.nivel]} style={{ fontSize: 10, flexShrink: 0 }}>
                  {NIVEL_LABEL[n.nivel]}
                </Tag>
              </div>

              <p style={{ fontSize: 12, color: '#595959', margin: '0 0 6px' }}>
                {n.mensaje}
              </p>

              {/* Nivel 2: botón confirmar */}
              {n.requiereConfirmacion && !n.confirmada && (
                <Button
                  size="small" type="primary"
                  icon={<CheckOutlined />}
                  onClick={(e) => confirmar(n._id, e)}
                  style={{ marginBottom: 4 }}
                >
                  Confirmar recepción
                </Button>
              )}
              {n.confirmada && (
                <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 11 }}>
                  Confirmada
                </Tag>
              )}

              {/* Nivel 3: checklist */}
              {n.checklist?.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  {n.checklist.map((item, idx) => (
                    <div key={idx} style={{ fontSize: 11, color: item.completado ? '#059669' : '#595959', lineHeight: '18px' }}>
                      {item.completado ? '✅' : '⬜'} {item.item}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ fontSize: 11, color: '#BFBFBF', marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
                <span>
                  {new Date(n.createdAt).toLocaleDateString('es-CO', {
                    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                  })}
                </span>
                {n.entidadUrl && (
                  <Button
                    type="link" size="small" style={{ fontSize: 11, padding: 0, height: 'auto' }}
                    onClick={(e) => { e.stopPropagation(); window.location.href = n.entidadUrl }}
                  >
                    Ver →
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '8px 16px', borderTop: '1px solid #F0F0F0', textAlign: 'center' }}>
        <Button
          type="link" size="small"
          onClick={() => { setOpen(false); window.location.href = '/notifications/config' }}
        >
          Configurar notificaciones →
        </Button>
      </div>
    </div>
  )

  return (
    <Dropdown
      dropdownRender={() => panel}
      trigger={['click']}
      open={open}
      onOpenChange={(v) => { setOpen(v); if (v) loadNotifs() }}
      placement="bottomRight"
    >
      <Badge count={count} size="small" offset={[-2, 2]}>
        <BellOutlined
          style={{
            fontSize: 20,
            cursor: 'pointer',
            color: count > 0 ? '#F59E0B' : '#fff',
          }}
        />
      </Badge>
    </Dropdown>
  )
}
